import type { FunctionComponent } from 'react'

import { FlowPage } from '@monthio/design-system'
import {
	LoadingSpinner,
	notificationService,
	trackingService,
} from '@monthio/react-components'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { ProcessingStatuses } from '../../types/authenticateSmartcheckSessionDto'
import { DocumentsUploader } from '../Budget/Components/documentsUploader'
import { ConfirmationScreen } from './Components/ConfirmationScreen'
import { DocumentUploadIcon } from './Components/DocumentUploadIcon'
import {
	CaseProcessingStatusEnum,
	FilesUploadStatusEnum,
} from './DocumentUpload.types'
import { useDocumentUploadModule } from './useDocumentUploadModule'

export const DocumentationRequestDocumentTypes = {
	Custom: 'custom',
	JobContract: 'jobContract',
	Payslip: 'payslip',
	SalaryChange: 'salaryChange',
	RentPayment: 'rentPayment',
	BusinessActivity: 'businessActivity',
	Savings: 'savings',
	PensionsInfo: 'pensionsInfo',
}
export type DocumentationRequestDocumentType =
	(typeof DocumentationRequestDocumentTypes)[keyof typeof DocumentationRequestDocumentTypes]

const DocumentUploadWrapper = styled.ul`
	display: flex;
	flex-direction: column;
	gap: 24px;
`

const DocumentUploadSection: FunctionComponent<{
	documentType: DocumentationRequestDocumentType
	setFiles: React.Dispatch<React.SetStateAction<Record<string, File[]>>>
	files: Record<string, File[]>
}> = ({ documentType, files, setFiles }) => {
	const { t } = useTranslation()

	const handleDocumentsUpload = useCallback(
		(files: File[]) => {
			setFiles((prev) => {
				const newState = { ...prev }
				newState[documentType] = files

				return newState
			})
		},
		[documentType, setFiles],
	)

	return (
		<div>
			<li>{t(`documentUpload-documentType-${documentType}`)}</li>
			<DocumentsUploader
				uploadedFiles={files[documentType]}
				handleChange={handleDocumentsUpload}
			/>
		</div>
	)
}

export const DocumentUpload: FunctionComponent = () => {
	const { config, data } = useDocumentUploadModule()

	const [fileUploadStatus, setFileUploadStatus] =
		useState<FilesUploadStatusEnum>(FilesUploadStatusEnum.IDLE)
	const [caseProcessingStatus, setCaseProcessingStatus] =
		useState<CaseProcessingStatusEnum>(CaseProcessingStatusEnum.IDLE)
	const { t } = useTranslation()
	const [files, setFiles] = useState<
		Record<DocumentationRequestDocumentType, File[]>
	>({})

	const allFiles = Object.values(files).flat()

	const handleSubmit = useCallback(async () => {
		try {
			setFileUploadStatus(FilesUploadStatusEnum.UPLOADING)
			await config.uploadFiles(allFiles)
			setFileUploadStatus(FilesUploadStatusEnum.UPLOAD_SUCCESS)
		} catch (error: any) {
			setFileUploadStatus(FilesUploadStatusEnum.UPLOAD_FAILURE)
		}
	}, [allFiles, config.uploadFiles])

	useEffect(() => {
		if (
			fileUploadStatus !== FilesUploadStatusEnum.UPLOAD_SUCCESS ||
			data.caseId === undefined
		) {
			return
		}

		const run = async () => {
			try {
				setCaseProcessingStatus(CaseProcessingStatusEnum.UPDATING)
				await config.updateCaseProcessingStatus(
					data.caseId,
					ProcessingStatuses.DocumentationReceived,
				)
				setCaseProcessingStatus(CaseProcessingStatusEnum.UPDATE_SUCCESS)
			} catch {
				setCaseProcessingStatus(CaseProcessingStatusEnum.UPDATE_FAILURE)
			}
		}

		run()
	}, [fileUploadStatus, data.caseId, config.updateCaseProcessingStatus])

	const handleCustomDocumentsChange = useCallback(
		(files) =>
			setFiles((prev) => {
				const newState = { ...prev }
				newState[DocumentationRequestDocumentTypes.Custom] = files

				return newState
			}),
		[],
	)

	if (
		fileUploadStatus === FilesUploadStatusEnum.UPLOAD_FAILURE ||
		caseProcessingStatus === CaseProcessingStatusEnum.UPDATE_FAILURE
	) {
		notificationService.error(t('documentUpload-submitting-error'))
	}

	if (caseProcessingStatus === CaseProcessingStatusEnum.UPDATE_SUCCESS) {
		trackingService.trackEvent('additionalDocumentationReceived')
		return <ConfirmationScreen text={t('documentUpload-submitting-success')} />
	}

	const isCustomDocumentType =
		!data.documentTypes?.length ||
		data.documentTypes.includes(DocumentationRequestDocumentTypes.Custom)

	const translationKey = isCustomDocumentType
		? 'documentUpload-withoutDocumentType-subtitle'
		: 'documentUpload-withDocumentTypes-subtitle'

	const filesUploading = fileUploadStatus === FilesUploadStatusEnum.UPLOADING

	return (
		<FlowPage>
			<FlowPage.Content $spacing="xs">
				<FlowPage.Header
					title={t('documentUpload-title')}
					description={t(translationKey, {
						clientName: data.clientName,
					})}
					slot={<DocumentUploadIcon />}
				/>
				{isCustomDocumentType ? (
					<DocumentsUploader
						uploadedFiles={files[DocumentationRequestDocumentTypes.Custom]}
						handleChange={handleCustomDocumentsChange}
					/>
				) : (
					<DocumentUploadWrapper>
						{data.documentTypes.map((documentType) => (
							<DocumentUploadSection
								key={documentType}
								documentType={documentType}
								files={files}
								setFiles={setFiles}
							/>
						))}
					</DocumentUploadWrapper>
				)}
			</FlowPage.Content>
			<FlowPage.Footer
				actions={
					<FlowPage.Cta
						disabled={allFiles.length === 0 || filesUploading}
						onClick={handleSubmit}
					>
						<AsyncActionContent isLoading={filesUploading}>
							{t('incomeVerification-continue-text')}
						</AsyncActionContent>
					</FlowPage.Cta>
				}
			/>
		</FlowPage>
	)
}

const SpinnerContainer = styled.div`
	position: relative;
	content: '';
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
`

const Spinner = styled(LoadingSpinner)<{ $isVisible?: boolean }>`
	display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);

	& > * {
		// This is a HACK to make the spinner white. Remove as soon as a better solution is available!
		--primary: white;
	}
`

const LabelContainer = styled.span<{ $hidden?: boolean }>`
	visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
`

interface AsyncActionContentProps {
	isLoading: boolean
	children: string
}

const AsyncActionContent = ({
	isLoading,
	children,
}: AsyncActionContentProps) => {
	return (
		<SpinnerContainer>
			<Spinner $isVisible={isLoading} />
			<LabelContainer $hidden={isLoading}>{children}</LabelContainer>
		</SpinnerContainer>
	)
}
